import { useState } from 'react';

interface FinalCTAProps {
  heading?: string;
  subheading?: string;
  buttonLabel?: string;
}

export default function FinalCTA({
  heading = 'Protect Your Space.\nElevate Your View.',
  subheading = 'Get a custom quote for window tinting — automotive, residential, or commercial. No obligation, response within 24 hours.',
  buttonLabel = 'Request Your Free Quote',
}: FinalCTAProps) {
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [charCount, setCharCount] = useState(0);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSubmitting(true);
    const form = e.currentTarget;
    const data = new URLSearchParams(new FormData(form) as unknown as Record<string, string>);
    try {
      await fetch('https://readdy.ai/api/form/d75ve27lpdqvlupfpmbg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: data.toString(),
      });
      setSubmitted(true);
    } catch {
      setSubmitted(true);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="estimate" className="relative bg-[#080808] overflow-hidden">
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-[#AAFF00]/20 to-transparent" />
        <div className="absolute top-1/2 left-0 -translate-y-1/2 w-[400px] h-[600px] bg-[#AAFF00]/3 blur-[120px] rounded-full" />
      </div>

      <div className="flex flex-col lg:flex-row min-h-[700px]">
        {/* Left */}
        <div className="relative w-full lg:w-[48%] min-h-[420px] lg:min-h-0 overflow-hidden shrink-0">
          <img
            src="https://public.readdy.ai/ai/img_res/3b4780fd75b9bd9e2e7689204f4bc5a7.jpg"
            alt="Premium window tinting result by TINTSPRO X"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-[#080808]/30 to-[#080808]/85" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#080808] via-transparent to-[#080808]/30" />

          <div className="absolute inset-0 flex flex-col justify-end p-10 md:p-14">
            <div className="flex items-center gap-3 mb-5">
              <div className="w-8 h-[1px] bg-[#AAFF00]" />
              <span className="text-[#AAFF00] text-[11px] font-sans font-medium tracking-[0.3em] uppercase">Free Quote</span>
            </div>
            <h2 className="font-serif text-3xl md:text-4xl xl:text-5xl font-bold text-white leading-[1.1] mb-4 whitespace-pre-line">
              {heading}
            </h2>
            <p className="text-white/55 font-sans text-base leading-relaxed max-w-xs">{subheading}</p>
            <div className="flex flex-col gap-3 mt-8">
              <a
                href="tel:2155949404"
                className="flex items-center gap-3 text-white/60 text-sm font-sans hover:text-[#AAFF00] transition-colors cursor-pointer"
              >
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-phone-fill text-[#AAFF00] text-sm"></i>
                </div>
                (215) 594-9404
              </a>
              <a
                href="mailto:tintspro2@gmail.com"
                className="flex items-center gap-3 text-white/60 text-sm font-sans hover:text-[#AAFF00] transition-colors cursor-pointer"
              >
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-mail-fill text-[#AAFF00] text-sm"></i>
                </div>
                tintspro2@gmail.com
              </a>
              <div className="flex items-center gap-3 text-white/40 text-sm font-sans">
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-map-pin-line text-[#AAFF00] text-sm"></i>
                </div>
                3001 Ormes St Unit 05, Philadelphia, PA 19134
              </div>
            </div>
          </div>
        </div>

        {/* Right: Form */}
        <div className="flex-1 flex items-center justify-center px-8 md:px-14 py-16 relative z-10">
          {submitted ? (
            <div className="text-center max-w-sm">
              <div className="w-16 h-16 flex items-center justify-center mx-auto mb-6 bg-[#AAFF00]/15 rounded-full border border-[#AAFF00]/30">
                <i className="ri-checkbox-circle-fill text-[#AAFF00] text-3xl"></i>
              </div>
              <h3 className="font-serif text-white text-3xl font-bold mb-3">Quote Requested!</h3>
              <p className="text-white/45 font-sans text-base">
                We&apos;ll contact you within 24 hours to discuss your tinting needs and options.
              </p>
              <a
                href="tel:2155949404"
                className="mt-6 inline-flex items-center gap-2 text-[#AAFF00] text-sm font-sans hover:underline cursor-pointer"
              >
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-phone-fill text-sm"></i>
                </div>
                Or call (215) 594-9404
              </a>
            </div>
          ) : (
            <form
              data-readdy-form
              onSubmit={handleSubmit}
              className="w-full max-w-[400px] space-y-4"
            >
              <div className="mb-7">
                <h3 className="font-serif text-white text-2xl md:text-3xl font-bold mb-1">Get Your Free Quote</h3>
                <p className="text-white/35 text-sm font-sans">Response within 24 hours. No obligation.</p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-white/40 text-[11px] font-sans font-medium tracking-[0.15em] uppercase block mb-1.5">Full Name</label>
                  <input
                    type="text" name="name" required placeholder="Your name"
                    className="w-full bg-[#111111] border border-white/8 rounded-xl px-4 py-3 text-white text-sm font-sans placeholder:text-white/20 focus:outline-none focus:border-[#AAFF00]/50 transition-colors"
                  />
                </div>
                <div>
                  <label className="text-white/40 text-[11px] font-sans font-medium tracking-[0.15em] uppercase block mb-1.5">Phone</label>
                  <input
                    type="tel" name="phone" required placeholder="(215) 000-0000"
                    className="w-full bg-[#111111] border border-white/8 rounded-xl px-4 py-3 text-white text-sm font-sans placeholder:text-white/20 focus:outline-none focus:border-[#AAFF00]/50 transition-colors"
                  />
                </div>
              </div>

              <div>
                <label className="text-white/40 text-[11px] font-sans font-medium tracking-[0.15em] uppercase block mb-1.5">Email</label>
                <input
                  type="email" name="email" required placeholder="your@email.com"
                  className="w-full bg-[#111111] border border-white/8 rounded-xl px-4 py-3 text-white text-sm font-sans placeholder:text-white/20 focus:outline-none focus:border-[#AAFF00]/50 transition-colors"
                />
              </div>

              <div>
                <label className="text-white/40 text-[11px] font-sans font-medium tracking-[0.15em] uppercase block mb-1.5">Service</label>
                <select
                  name="project_type" required defaultValue=""
                  className="w-full bg-[#111111] border border-white/8 rounded-xl px-4 py-3 text-white text-sm font-sans focus:outline-none focus:border-[#AAFF00]/50 transition-colors cursor-pointer appearance-none"
                >
                  <option value="" disabled>Select a service</option>
                  <option value="Automotive Tinting">Automotive Tinting</option>
                  <option value="Commercial Tinting">Commercial Tinting</option>
                  <option value="Residential Tinting">Residential Tinting</option>
                  <option value="Ceramic Tinting">Ceramic Tinting</option>
                  <option value="Carbon Fiber Installation">Carbon Fiber Installation</option>
                  <option value="Window Film">Window Film</option>
                  <option value="Other">Other</option>
                </select>
              </div>

              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="text-white/40 text-[11px] font-sans font-medium tracking-[0.15em] uppercase block">Details & Notes</label>
                  <span className="text-white/25 text-[11px] font-sans">{charCount}/500</span>
                </div>
                <textarea
                  name="message" rows={3} maxLength={500}
                  placeholder="Vehicle make/model/year, address, or any project details..."
                  onChange={(e) => setCharCount(e.target.value.length)}
                  className="w-full bg-[#111111] border border-white/8 rounded-xl px-4 py-3 text-white text-sm font-sans placeholder:text-white/20 focus:outline-none focus:border-[#AAFF00]/50 transition-colors resize-none"
                />
              </div>

              <button
                type="submit" disabled={submitting}
                className="whitespace-nowrap w-full flex items-center justify-center gap-2 bg-[#AAFF00] text-[#080808] text-sm font-sans font-semibold py-4 rounded-full hover:bg-[#C4FF33] transition-all duration-300 cursor-pointer disabled:opacity-60 mt-2"
              >
                {submitting ? 'Sending...' : buttonLabel}
                {!submitting && (
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-arrow-right-line text-sm"></i>
                  </div>
                )}
              </button>

              <div className="flex items-center justify-center gap-2 pt-1">
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-lock-line text-white/25 text-xs"></i>
                </div>
                <p className="text-white/25 text-[11px] font-sans">Secure · No spam · Response within 24 hours</p>
              </div>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}
